// Container component: manages fetching, filters, and pagination (zero-based internally).
import React, { useEffect, useState } from 'react';
import CertificatesPage from './CertificatesPage';
import { showToast } from '../../helpers/showToast';
import { exportCertificatesExcel, getAllCertificates , revokeCertificate} from '../../api/methods';
import { Modal } from '../../pages/AdminControl/components/Modal';


// If your backend expects 1-based pages, keep this true.
// It will send (uiPage + 1) to the API while keeping UI zero-based.
const API_PAGE_IS_ONE_BASED = true;

const CertificatesPageContainer = ({ tab }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const [filters, setFilters] = useState({
    serialNumber: '',
    status: '',
    taxNumber: '',
    issuedFromStart: '',
    issuedFromEnd: '',
    expiryFromStart: '',
    expiryFromEnd: '',
    // activityNumber can be injected by UI; not setting default is fine
  });

  const [pagination, setPagination] = useState({
    page: 0, // zero-based internally; UI shows this as 1
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });

  const [statusOptions] = useState([
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Revoked', value: 'REVOKED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ]);

  const fetchCertificates = async (page = pagination.page, size = pagination.size, customFilters = null) => {
    setLoading(true);
    try {
      const filtersToUse = customFilters || filters;

      // Map UI zero-based page to API page if needed
      const apiPage = API_PAGE_IS_ONE_BASED ? (Number.isFinite(page) ? page + 1 : 1) : page;

      const queryFilters = {
        ...filtersToUse,
        page: apiPage,
        size,
      };

      // Remove empty string/null/undefined but keep 0 and keep 1-based page
      Object.keys(queryFilters).forEach((key) => {
        const v = queryFilters[key];
        if (v === '' || v === null || v === undefined) {
          delete queryFilters[key];
        }
      });

      const response = await getAllCertificates(queryFilters);
      const data = response?.data || response;

      if (data && Array.isArray(data.content)) {
        setCertificates(data.content);

        const totalElements =
          typeof data.totalElements === 'number'
            ? data.totalElements
            : typeof data.total === 'number'
              ? data.total
              : 0;

        const effectiveSize = typeof data.size === 'number' && data.size > 0 ? data.size : size;

        const totalPages =
          typeof data.totalPages === 'number'
            ? data.totalPages
            : totalElements > 0 && effectiveSize > 0
              ? Math.ceil(totalElements / effectiveSize)
              : 0;

        // Clamp the UI page to valid range (still zero-based internally)
        const maxPage = Math.max(0, totalPages - 1);
        const requestedUiPage = Number.isFinite(page) ? page : 0;
        const normalizedPage = Math.min(Math.max(0, requestedUiPage), maxPage);

        setPagination({
          page: normalizedPage,
          size: effectiveSize,
          totalElements,
          totalPages,
        });
      } else {
        console.warn('Invalid certificates response structure:', data);
        setCertificates([]);
        setPagination((prev) => ({ ...prev, totalElements: 0, totalPages: 0, page: 0 }));
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
      showToast({
        toastOptions: { type: 'error' },
        data: { variant: 'error', text: 'Failed to load certificates. Please try again later.' },
      });
    } finally {
      setLoading(false);
    }
  };

const handleRevokeCertificate = (serialNumber) => {
    const certificate = certificates.find(cert => cert.serialNumber === serialNumber);
    setSelectedCertificate(certificate || { serialNumber });
    setShowRevokeModal(true);
  };

  const closeRevokeModal = () => {
    setShowRevokeModal(false);
    setSelectedCertificate(null);
  };

  const confirmRevokeCertificate = async (note) => {
    if (!selectedCertificate) return;

    closeRevokeModal();
    setLoading(true);

    try {
      // If your API expects the note to be sent, you might need to pass it
      // Check your API method signature - it might need to be:
      // await revokeCertificate(selectedCertificate.serialNumber, { note });
      // or
      // await revokeCertificate(selectedCertificate.serialNumber, note);
      
      await revokeCertificate(selectedCertificate.id , { note });
      
      showToast({
        toastOptions: { type: 'success' },
        data: { variant: 'success', text: 'Certificate revoked successfully' },
      });
      fetchCertificates(pagination.page, pagination.size);
    } catch (error) {
      console.error('Error revoking certificate:', error);
      showToast({
        toastOptions: { type: 'error' },
        data: { variant: 'error', text: 'Failed to revoke certificate' },
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Initial UI page is 0 (shows as "1" in UI); API receives 1 due to mapping above
    fetchCertificates(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (arg1, arg2) => {
    // Support different pagination component signatures
    let incomingPage;

    if (typeof arg1 === 'number') {
      // Direct zero-based page index
      incomingPage = arg1;
    } else if (typeof arg2 === 'number') {
      // MUI Pagination (event, value) where value is 1-based
      const value = arg2;
      if (pagination?.totalPages && value >= 1 && value <= pagination.totalPages) {
        incomingPage = value - 1; // convert to zero-based
      } else {
        incomingPage = value - 1; // still convert; will clamp below
      }
    } else {
      return; // invalid call
    }

    const maxPage = Math.max(0, (pagination.totalPages || 1) - 1);
    const nextPage = Math.min(Math.max(0, incomingPage), maxPage);

    if (nextPage === pagination.page) return;

    fetchCertificates(nextPage, pagination.size);
  };

  const handlePageSizeChange = (arg) => {
    let newSize;

    if (typeof arg === 'number') {
      newSize = arg;
    } else if (arg && typeof arg === 'object' && arg.target && arg.target.value) {
      newSize = parseInt(arg.target.value, 10);
    }

    if (!Number.isFinite(newSize) || newSize <= 0) {
      newSize = pagination.size || 20;
    }

    // Reset to first page (UI 0 => API 1) when page size changes
    fetchCertificates(0, newSize);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Reset to first page on filter changes
    fetchCertificates(0, pagination.size, newFilters);
  };

  const exportAsExcel = async () => {
    setLoading(true);
    try {
      const exportFilters = { ...filters, size: 1000 };

      Object.keys(exportFilters).forEach(key => {
        const v = exportFilters[key];
        if (v === '' || v === null || v === undefined) {
          delete exportFilters[key];
        }
      });

      const response = await exportCertificatesExcel(exportFilters);

      const href = URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = href;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `certificates-${date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      showToast({
        toastOptions: { type: 'success' }, data: {
          variant: 'success', text: 'Certificates exported successfully',
        },
      });
    } catch (error) {
      console.error('Error exporting Certificates:', error);
      showToast({
        toastOptions: { type: 'error' }, data: {
          variant: 'error', text: 'Failed to export Certificates. Please try again later.',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
   <>
      <CertificatesPage
        tab={tab}
        certificates={certificates}
        loading={loading}
        pagination={pagination}
        filters={filters}
        statusOptions={statusOptions}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        onExport={exportAsExcel}
        onRevokeCertificate={handleRevokeCertificate}
        onRefresh={() => fetchCertificates(pagination.page, pagination.size)}
      />

      {/* Revoke Certificate Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={closeRevokeModal}
        onConfirm={confirmRevokeCertificate}
        title="Revoke Certificate"
        content={
          <p>
            Are you sure you want to revoke this certificate? This action cannot be undone.
            <br />
            <strong>Serial Number:</strong> {selectedCertificate?.serialNumber}
          </p>
        }
        requireNote={true}
        noteLabel="Reason for revocation"
        notePlaceholder="Please provide a reason for revoking this certificate (minimum 4 characters)..."
        initialNote=""
      />
    </>
  );
};

export default CertificatesPageContainer;
